/* ============================================================
   spn-product-cart.js — 商品詳細ページの購入操作（Shopify 版）
   - 数量ステッパー / タブ / ギャラリー切替（静的サンプル script.js の該当部分の移植）
   - カートに入れる: /cart/add.js に送り、Horizon のカートドロワーへ更新を知らせて開く
     ・メイン基板接続タイプで選んだメーカー、コイン不要機で入力した機種名などは
       「行のプロパティ」として注文データに残す（配線・ハーネス選定に使うため）
   商品データは window.SPN_VARIANTS（spn-main-product が出力）、
   選択状態は URL（?conn= / ?variant= / ?maker=）と spn-products.js が描画したフォームから読む。
   ============================================================ */

import { CartLinesUpdateEvent } from '@shopify/events';

const VARIANTS = window.SPN_VARIANTS || [];
const PRODUCT = (window.SPN_PRODUCTS || [])[0] || {};
const ROUTES = window.SPN_ROUTES || { cart_add_url: '/cart/add', cart_url: '/cart' };

// ---- 数量ステッパー ------------------------------------------------
document.querySelectorAll('.spn .qty-stepper').forEach((stepper) => {
  const input = stepper.querySelector('input');
  if (!input) return;
  stepper.querySelector("[data-qty='minus']")?.addEventListener('click', () => {
    const v = parseInt(input.value, 10) || 1;
    if (v > 1) input.value = v - 1;
  });
  stepper.querySelector("[data-qty='plus']")?.addEventListener('click', () => {
    input.value = (parseInt(input.value, 10) || 1) + 1;
  });
});

// ---- タブ切替 --------------------------------------------------------
document.querySelectorAll('.spn .product-tabs').forEach((tabs) => {
  const btns = tabs.querySelectorAll('button');
  const panels = document.querySelectorAll('.spn .product-tab-panel');
  btns.forEach((b) => {
    b.addEventListener('click', () => {
      btns.forEach((x) => x.classList.remove('is-active'));
      b.classList.add('is-active');
      panels.forEach((p) => { p.hidden = p.dataset.panel !== b.dataset.tab; });
    });
  });
});

// ---- ギャラリー切替（サムネイルを押すとメイン画像を差し替え） ---------
document.querySelectorAll('.spn [data-gallery-thumb]').forEach((thumb, _, all) => {
  thumb.addEventListener('click', () => {
    const main = document.querySelector('.spn [data-gallery-main] img');
    if (!main) return;
    main.src = thumb.dataset.src;
    main.removeAttribute('srcset');
    all.forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    // そのタイプ専用の写真なら、タイプ（と価格）も写真に合わせて選ぶ（2026-10-20 ユーザー要望）。
    // 「タイプを選ぶ」で選び直したときと同じ処理（spn-products.js の change → ?conn= で読み直し）に任せる
    const vid = thumb.dataset.variantId;
    if (!vid) return;
    const select = document.querySelector('.spn [data-variant-select]');
    const opt = select && [...select.options].find((o) => o.getAttribute('data-variant-id') === vid);
    if (opt) {
      if (opt.selected) return;
      select.value = opt.value;
      select.dispatchEvent(new Event('change'));
      return;
    }
    // 選択欄がまだ描画されていないとき：同じ規則（?conn= か ?variant=、maker は残す）で直接そのタイプのページへ
    const v = VARIANTS.find((x) => String(x.id) === vid);
    if (!v) return;
    const u = new URL(location.href);
    if (v.connection_type) { u.searchParams.set('conn', v.connection_type); u.searchParams.delete('variant'); }
    else { u.searchParams.set('variant', vid); u.searchParams.delete('conn'); }
    if (u.toString() !== location.href) location.href = u.toString();
  });
});

// ---- 選択中の variant -------------------------------------------------
function selectedVariant() {
  if (VARIANTS.length === 1) return VARIANTS[0];
  const params = new URLSearchParams(location.search);
  const conn = params.get('conn');
  const id = params.get('variant');
  if (conn) return VARIANTS.find((v) => v.connection_type === conn) || null;
  if (id) return VARIANTS.find((v) => String(v.id) === id) || null;
  return null;
}

// ---- 選択中タイプの写真をメインに表示 ---------------------------------
// ?conn= で開いた時はサーバー側で variant が決まらず 1 枚目の写真になるため、
// 選ばれたタイプの写真に差し替える（同じ写真のサムネイルがあればそれを選択状態に）
(function showSelectedVariantImage() {
  const v = selectedVariant();
  if (!v) return;
  // 画面下の固定購入バーの価格・LINE 相談の品番も選んだタイプに合わせる
  const price = document.querySelector('.spn [data-sticky-price]');
  if (price && typeof v.price === 'number') price.textContent = '¥' + v.price.toLocaleString('ja-JP');
  const line = document.querySelector('.spn .product-sticky-cta [data-product-code]');
  if (line && v.sku) line.setAttribute('data-product-code', v.sku);
  if (!v.image_url) return;
  const thumb = [...document.querySelectorAll('.spn [data-gallery-thumb]')].find((t) => t.dataset.src === v.image_url);
  if (thumb) { thumb.click(); return; }
  const main = document.querySelector('.spn [data-gallery-main] img');
  if (main) { main.src = v.image_url; main.removeAttribute('srcset'); }
})();

// ---- 注文データに残す入力内容 ------------------------------------------
function lineProperties() {
  const props = {};
  const makerSelect = document.querySelector('.spn [data-maker-select]');
  if (makerSelect) {
    // メーカーは任意（2026-10-20）。未選択なら当店で調べる旨を注文に残す
    props['メーカー'] = makerSelect.value ? makerSelect.options[makerSelect.selectedIndex].text : 'わからない（当店で確認）';
  }
  // メイン基板接続タイプの機種名（2026-10-19）
  const unitMachine = document.querySelector('.spn [data-unit-machine]');
  if (unitMachine && unitMachine.value.trim()) props['機種名'] = unitMachine.value.trim();
  const form = document.querySelector('.spn [data-coinless-form]');
  if (form) {
    const maker = form.querySelector('[data-cf-maker]');
    if (maker && maker.value) props['メーカー'] = maker.options[maker.selectedIndex].text;
    const machine = form.querySelector('[data-cf-machine]');
    if (machine && machine.value.trim()) props['機種名'] = machine.value.trim();
  }
  return props;
}

function quantity() {
  const input = document.querySelector('.spn .qty-stepper input');
  return Math.max(1, parseInt(input && input.value, 10) || 1);
}

function setButtonsText(text, busy) {
  document.querySelectorAll('.spn [data-add-to-cart]').forEach((btn) => {
    const label = btn.querySelector('[data-cart-label]');
    if (label) label.textContent = text;
    btn.classList.toggle('is-busy', !!busy);
  });
}

function showError(message) {
  const box = document.querySelector('.spn [data-cart-error]');
  if (!box) return;
  box.textContent = message;
  box.hidden = !message;
}

// 入力が足りずカートに入れられない時：理由を表示し、その入力欄へスクロールする
const BLOCK_GUIDE = {
  'variant': { msg: 'タイプを選んでください。', target: '[data-variant-select]' },
  'maker': { msg: 'メーカーを選んでください（メイン基板接続タイプは機種に合わせたハーネスが付属します）。', target: '[data-maker-select]' },
  'unit-machine': { msg: '機種名を入力してください（機種に合わせたハーネスをご用意します）。', target: '[data-unit-machine]' },
  'coinless-form': { msg: 'メーカーと機種名を入力し、「上記内容で間違いありません」にチェックを入れてください。', target: '[data-coinless-form]' },
};
function guideBlocked(btn) {
  const reason = (btn.dataset.cartBlockedBy || '').split(' ').find((r) => BLOCK_GUIDE[r]);
  const guide = BLOCK_GUIDE[reason] || { msg: '必要な項目を入力してください。', target: null };
  // メーカー欄の無いコイン不要機（機種名だけ入力）では「メーカーと」を外す
  const noMaker = reason === 'coinless-form' && !document.querySelector('.spn [data-cf-maker]');
  const msg = noMaker ? guide.msg.replace('メーカーと機種名', '機種名') : guide.msg;
  const el = guide.target && document.querySelector('.spn ' + guide.target);
  // 案内は 1 か所だけ：入力欄のそばに出せるならそちら、無ければカートボタンの下
  if (!el) { showError(msg); return; }
  showError('');
  // 移動先でも読めるよう、入力欄のそばにも同じ案内を出す（コイン不要機は確認枠の中、それ以外は選択欄の直後）
  document.querySelectorAll('.spn [data-cart-guide]').forEach((g) => g.remove());
  const note = document.createElement('p');
  note.setAttribute('data-cart-guide', '');
  note.setAttribute('role', 'alert');
  note.className = 'cart-guide';
  note.textContent = msg;
  const summary = el.querySelector('[data-cf-summary]');
  const confirm = summary && summary.querySelector('.cf-confirm');
  if (confirm) confirm.before(note); else el.after(note);
  const field = el.matches('select, input') ? el
    : [...el.querySelectorAll('select, input[type="text"]')].find((f) => !f.value.trim()) || el.querySelector('[data-cf-confirm]');
  // iPhone の Safari は「押した直後」でないとキーボードを出さないため、待たずにその場でフォーカスする
  if (field) field.focus({ preventScroll: true });
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function addToCart(btn) {
  if (btn.dataset.cartBlockedBy) { guideBlocked(btn); return; }
  if (btn.hasAttribute('disabled') || btn.classList.contains('is-busy')) return;
  const variant = selectedVariant();
  if (!variant) { guideBlocked({ dataset: { cartBlockedBy: 'variant' } }); return; }
  showError('');
  const qty = quantity();
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean);

  // Horizon のカート部品（件数・ドロワー）へ「追加します」を知らせる
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variant.id), quantity: qty }],
    promise: deferred.promise,
  }));

  setButtonsText('追加しています…', true);
  // 1. 追加そのもの。ここで通信が切れると「入ったか分からない」ので、押し直す前にカートの確認を促す
  //    （押し直しで 2 個入るのを防ぐ。2026-10-16 fusion 指摘）
  let res, json;
  try {
    res = await fetch(ROUTES.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variant.id, quantity: qty, properties: lineProperties() }],
        sections: sectionIds.join(','),
      }),
    });
    json = await res.json().catch(() => ({}));
  } catch (e) {
    deferred.reject(e);
    setButtonsText('カートに入れる', false);
    showError('通信が不安定なため、カートに入ったか確認できませんでした。もう一度押す前に、画面右上のカートをご確認ください。');
    return;
  }
  const ok = res.ok && !json.status;
  if (!ok) {
    // 追加に失敗：今のカートの中身で件数表示を正しく保つ（従来どおり didError で知らせる）
    fetch(ROUTES.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json())
      .then((cart) => deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'spn-product-cart', itemCount: qty, productId: PRODUCT.id, didError: true },
      }))
      .catch((e) => deferred.reject(e));
    setButtonsText('カートに入れる', false);
    showError(json.description || json.message || 'カートに追加できませんでした。');
    return;
  }
  // 2. 追加は成功。ここから先（件数・ドロワーの表示更新）が失敗しても「追加できなかった」とは言わない
  setButtonsText('カートに追加しました', false);
  setTimeout(() => setButtonsText('カートに入れる', false), 1800);
  try {
    const cart = await fetch(ROUTES.cart_url + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'spn-product-cart',
        itemCount: qty,
        productId: PRODUCT.id,
        sections: json.sections,
        didError: false,
      },
    });
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  } catch (e) {
    deferred.reject(e);
    showError('カートに追加しました。カートの表示が更新されない場合は、ページを再読み込みしてください。');
  }
}

document.querySelectorAll('.spn [data-add-to-cart]').forEach((btn) => {
  btn.addEventListener('click', () => addToCart(btn));
});
